/**
 * PayrollDialog - Create payroll record dialog
 * Extracted from HRPayrollPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign } from "lucide-react";

const payPeriodOptions = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "semimonthly", label: "Semi-Monthly" },
  { value: "monthly", label: "Monthly" },
];

export function PayrollDialog({
  open,
  onOpenChange,
  employees,
  newPayroll,
  setNewPayroll,
  saving,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Create Payroll Record
          </DialogTitle>
          <DialogDescription>
            Enter payroll details for an employee
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select
                  value={newPayroll.employee_id}
                  onValueChange={(v) => setNewPayroll(prev => ({ ...prev, employee_id: v }))}
                >
                  <SelectTrigger data-testid="payroll-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pay Period Type</Label>
                <Select
                  value={newPayroll.pay_period_type || "biweekly"}
                  onValueChange={(v) => setNewPayroll(prev => ({ ...prev, pay_period_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {payPeriodOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pay Period Start *</Label>
                <Input
                  type="date"
                  value={newPayroll.pay_period_start}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, pay_period_start: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Pay Period End *</Label>
                <Input
                  type="date"
                  value={newPayroll.pay_period_end}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, pay_period_end: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hours Worked</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newPayroll.hours_worked}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, hours_worked: e.target.value }))}
                  placeholder="40"
                />
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayroll.hourly_rate}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, hourly_rate: e.target.value }))}
                  placeholder="15.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Overtime Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={newPayroll.overtime_hours}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, overtime_hours: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Overtime Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayroll.overtime_rate}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, overtime_rate: e.target.value }))}
                  placeholder="22.50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deductions ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayroll.deductions}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, deductions: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Bonuses ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayroll.bonuses}
                  onChange={(e) => setNewPayroll(prev => ({ ...prev, bonuses: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newPayroll.notes}
                onChange={(e) => setNewPayroll(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Create Record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
